import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  Card,
  CardMedia,
  CardActions,
} from "@mui/material";
import {
  useCreatePub,
  useUpdatePub,
  Pub as PubType,
  UpdatePubRequest,
  PUB_DEFAULT_RADIUS_KM,
  PUB_MAX_RADIUS_KM,
} from "../../../api/pub";
import { useEtablissementOptions } from "../../../api/etablissement";
import { CustomLoader } from "../../../components/CustomLoader/CustomLoader";
import { CentreAutocomplete } from "../../../components/CentreAutocomplete/CentreAutocomplete";
import { AddressAutocomplete } from "../../../components/AddressAutocomplete/AddressAutocomplete";
import { CustomDropZoneUpload } from "../../../components/CustomDropZoneUpload/CustomDropZoneUpload";
import { uploadToStorage } from "../../../api/media";
import toast from "react-hot-toast";
import { Icon } from "../../../components/Icon/Icon";
import { colors } from "../../../styles/colors";
import useStyles from "./styles";

interface ModalAddPubProps {
  open: boolean;
  pubInitial: PubType;
  onClose: () => void;
  onCancel: () => void;
}

/**
 * The `signs` prop is GONE, deliberately.
 *
 * It carried the entire centre catalogue down from <Pubs>, which had to fetch it
 * on every visit to the page whether or not this modal was ever opened. The
 * centre picker now fetches what it needs, when it is opened, filtered by what
 * the operator typed - see CentreAutocomplete. Passing the list down again would
 * reinstate the page-load cost the search was introduced to remove.
 */
export const ModalAddPub: React.FC<ModalAddPubProps> = ({
  open,
  pubInitial,
  onClose,
  onCancel,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isEdit = pubInitial.id !== -1;

  const [formData, setFormData] = useState<Partial<PubType>>({
    name: "",
    description: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    postal_code: "",
    latitude: null,
    longitude: null,
    radius_km: PUB_DEFAULT_RADIUS_KM,
    main_image: "",
    pub_type: "free",
    start_date: null,
    end_date: null,
    is_active: true,
    display_order: null,
    sign_id: null,
    etablissement_id: null,
  });

  /*
   * Le référentiel des enseignes n'est chargé QUE quand la modale est ouverte.
   *
   * C'est la même leçon que celle qui a fait disparaître la prop `signs` : la
   * page ne doit rien payer pour un formulaire que l'opérateur n'ouvrira
   * peut-être jamais. La différence avec les centres est la taille du
   * référentiel (des dizaines de lignes contre des dizaines de milliers), ce
   * qui autorise ici un <Select> plein là où les centres exigent une recherche
   * serveur — voir useEtablissementOptions.
   */
  const { etablissements } = useEtablissementOptions(open);

  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [mainImagePreview, setMainImagePreview] = useState<string>("");
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);

  const createPubMutation = useCreatePub();
  const updatePubMutation = useUpdatePub();

  useEffect(() => {
    if (open && pubInitial) {
      setFormData({
        name: pubInitial.name || "",
        description: pubInitial.description || "",
        phone: pubInitial.phone || "",
        email: pubInitial.email || "",
        address: pubInitial.address || "",
        city: pubInitial.city || "",
        postal_code: pubInitial.postal_code || "",
        latitude: pubInitial.latitude,
        longitude: pubInitial.longitude,
        /*
         * `?? PUB_DEFAULT_RADIUS_KM` et non `|| ...` : `null` en base signifie
         * « diffusion sans limite », une valeur qu'un opérateur a pu choisir
         * délibérément. `||` l'aurait écrasée à 50 à chaque ouverture du
         * formulaire, donc à chaque enregistrement — une campagne nationale
         * serait redevenue locale sans que personne n'ait touché au champ.
         * Seul `undefined` (création) retombe sur le défaut.
         */
        radius_km:
          pubInitial.radius_km === undefined
            ? PUB_DEFAULT_RADIUS_KM
            : pubInitial.radius_km,
        main_image: pubInitial.main_image || "",
        pub_type: pubInitial.pub_type || "free",
        start_date: pubInitial.start_date,
        end_date: pubInitial.end_date,
        is_active: pubInitial.is_active ?? true,
        display_order: pubInitial.display_order,
        sign_id: pubInitial.sign_id,
        etablissement_id: pubInitial.etablissement_id,
        gallery_images: pubInitial.gallery_images || [],
      });
      
      setMainImagePreview(pubInitial.main_image || "");
      setGalleryPreviews(pubInitial.gallery_images || []);
    } else {
      // Reset state when opening for new pub
      setFormData({
        name: "",
        description: "",
        phone: "",
        email: "",
        address: "",
        city: "",
        postal_code: "",
        latitude: null,
        longitude: null,
        radius_km: PUB_DEFAULT_RADIUS_KM,
        main_image: "",
        pub_type: "free",
        start_date: null,
        end_date: null,
        is_active: true,
        display_order: null,
        sign_id: null,
        etablissement_id: null,
        gallery_images: [],
      });
      setMainImagePreview("");
      setGalleryPreviews([]);
    }
  }, [open, pubInitial]);

  const handleChange = (field: keyof PubType, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle main image upload (MinIO object storage via backend presigned URL)
  const handleMainImageUpload = async (files: File[]) => {
    if (files.length === 0) return;
    
    const file = files[0];
    setUploadingMain(true);
    
    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setMainImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // "public/pub" (not "pub"): the URL is persisted on the pub record, so
      // it must land in the anonymously-readable prefix or it 403s on display.
      const { url: imageUrl } = await uploadToStorage(file, {
        directory: "public/pub",
      });
      
      if (imageUrl) {
        handleChange("main_image", imageUrl);
        toast.success(t("pub.mainImageUploaded", "Main image uploaded successfully"));
      } else {
        throw new Error("Upload failed");
      }
    } catch (error: any) {
      console.error("Error uploading main image:", error);
      toast.error(
        error?.message ||
          t("pub.mainImageUploadError", "Failed to upload main image")
      );
      setMainImagePreview("");
    } finally {
      setUploadingMain(false);
    }
  };

  // Handle gallery image upload (MinIO object storage via backend presigned URL)
  const handleGalleryImageUpload = async (files: File[]) => {
    if (files.length === 0) return;
    
    const file = files[0];
    setUploadingGallery(true);
    
    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setGalleryPreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);

      // "public/pub" — see handleMainImageUpload for why.
      const { url: imageUrl } = await uploadToStorage(file, {
        directory: "public/pub",
      });
      
      if (imageUrl) {
        const currentGallery = (formData.gallery_images || []) as string[];
        const updatedGallery: string[] = [...currentGallery, imageUrl as string];
        
        setFormData((prev) => ({
          ...prev,
          gallery_images: updatedGallery,
        }));
        
        toast.success(t("pub.galleryImageAdded", "Gallery image added successfully"));
      } else {
        throw new Error("Upload failed");
      }
    } catch (error: any) {
      console.error("Error uploading gallery image:", error);
      toast.error(
        error?.message ||
          t("pub.galleryImageUploadError", "Failed to upload gallery image")
      );
      // Remove the preview if upload failed
      setGalleryPreviews((prev) => prev.slice(0, -1));
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleRemoveMainImage = () => {
    setMainImagePreview("");
    handleChange("main_image", "");
  };

  const handleRemoveGalleryImage = (index: number) => {
    const currentGallery = (formData.gallery_images || []) as string[];
    setFormData((prev) => ({
      ...prev,
      gallery_images: currentGallery.filter((_, i) => i !== index),
    }));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    // Validation
    if (!formData.name || formData.name.trim() === "") {
      toast.error(t("pub.nameRequired", "Pub name is required"));
      return;
    }

    /*
     * Le rayon est validé ICI en plus de l'API.
     *
     * Pas par défiance envers le serveur — `Pub::MAX_RADIUS_KM` reste
     * l'autorité — mais parce qu'un 422 sur ce champ précis arrive après
     * l'upload des images et ferait repartir l'opérateur d'un formulaire
     * complet pour une valeur qu'on pouvait refuser tout de suite.
     */
    const radiusRaw = formData.radius_km;
    if (radiusRaw !== null && radiusRaw !== undefined && String(radiusRaw) !== "") {
      const radius = Number(radiusRaw);
      if (!Number.isFinite(radius) || radius < 1 || radius > PUB_MAX_RADIUS_KM) {
        toast.error(
          t("pub.radiusInvalid", {
            max: PUB_MAX_RADIUS_KM,
            defaultValue: `Le rayon doit être compris entre 1 et ${PUB_MAX_RADIUS_KM} km (laissez vide pour une diffusion sans limite)`,
          })
        );
        return;
      }
    }

    /*
     * `?? undefined` on strings, but explicit `null` for sign_id, and a real
     * emptiness test for the numerics.
     *
     * Every field here used to be `formData.x || undefined`, which conflates
     * "not set" with three legitimate values:
     *
     *   sign_id: 0/null -> the key vanished from the body, so the backend's
     *     fill() never cleared the column. Choosing "General Pub (No Sign)" on
     *     an assigned pub appeared to save and changed nothing. Detaching from a
     *     centre was IMPOSSIBLE from this form.
     *   display_order: 0 -> dropped, so the highest-priority position could not
     *     be set at all.
     *   latitude / longitude: 0 -> dropped. 0 is a real coordinate (Gulf of
     *     Guinea, and the Greenwich meridian runs through France and Spain).
     */
    const numberOrUndefined = (value: unknown): number | undefined => {
      if (value === null || value === undefined || value === "") return undefined;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const submitData: UpdatePubRequest = {
      name: formData.name || undefined,
      description: formData.description || undefined,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      address: formData.address || undefined,
      city: formData.city || undefined,
      postal_code: formData.postal_code || undefined,
      main_image: formData.main_image || undefined,
      pub_type: formData.pub_type,
      is_active: formData.is_active,
      latitude: numberOrUndefined(formData.latitude),
      longitude: numberOrUndefined(formData.longitude),
      display_order: numberOrUndefined(formData.display_order),
      // null, NOT undefined: null is the instruction to detach.
      sign_id: numberOrUndefined(formData.sign_id) ?? null,
      // Même contrat, côté enseigne : champ vidé -> null -> détacher du réseau.
      // Avec `|| undefined` la clé disparaîtrait du corps JSON et le fill()
      // côté API ne remettrait jamais la colonne à NULL — une campagne serait
      // rattachable à une enseigne et jamais détachable depuis ce formulaire.
      etablissement_id: numberOrUndefined(formData.etablissement_id) ?? null,
      // Même contrat que sign_id : champ vide -> null -> « sans limite de
      // distance ». Avec `|| undefined` la clé disparaîtrait et le rayon ne
      // pourrait jamais être levé après coup.
      radius_km: numberOrUndefined(formData.radius_km) ?? null,
      start_date: formData.start_date || undefined,
      end_date: formData.end_date || undefined,
      gallery_images: formData.gallery_images && (formData.gallery_images as string[]).length > 0 
        ? formData.gallery_images 
        : undefined,
    };

    if (isEdit) {
      updatePubMutation.mutate(
        { id: pubInitial.id, data: submitData },
        {
          onSuccess: () => {
            toast.success(t("pub.pubUpdated", "Pub updated successfully"));
            onClose();
          },
          onError: (error: any) => {
            toast.error(
              error?.response?.data?.message || 
              t("pub.updateError", "Failed to update pub")
            );
          },
        }
      );
    } else {
      createPubMutation.mutate(submitData, {
        onSuccess: () => {
          toast.success(t("pub.pubCreated", "Pub created successfully"));
          onClose();
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message || 
            t("pub.createError", "Failed to create pub")
          );
        },
      });
    }
  };

  const isPending = createPubMutation.isPending || updatePubMutation.isPending || uploadingMain || uploadingGallery;

  return (
    <>
      {isPending && <CustomLoader isActive={isPending} />}
      
      <Dialog 
        open={open} 
        onClose={onCancel} 
        maxWidth="lg" 
        fullWidth
        className={classes.dialog}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box display="flex" alignItems="center" gap={2}>
            <Icon 
              name={isEdit ? "edit" : "plus"} 
              width="24px" 
              height="24px" 
              color={colors.primary} 
            />
            <Typography variant="h5" component="span" fontWeight={600}>
              {isEdit ? t("pub.editPub", "Edit Pub") : t("pub.addPub", "Add Pub")}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={3}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Box className={classes.sectionHeader}>
                <Icon name="info" width="20px" height="20px" color={colors.primary} />
                <Typography variant="h6" fontWeight={600}>
                  {t("pub.basicInfo", "Basic Information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t("pub.name", "Name")}
                value={formData.name || ""}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder={t("pub.namePlaceholder", "Enter pub name")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("pub.type", "Type")}</InputLabel>
                <Select
                  value={formData.pub_type || "free"}
                  label={t("pub.type", "Type")}
                  onChange={(e) => handleChange("pub_type", e.target.value)}
                >
                  <MenuItem value="free">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Icon name="tag" width="16px" height="16px" color={colors.greydark} />
                      Free
                    </Box>
                  </MenuItem>
                  <MenuItem value="premium">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Icon name="star" width="16px" height="16px" color={colors.primary} />
                      Premium
                    </Box>
                  </MenuItem>
                  <MenuItem value="sponsored">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Icon name="trending-up" width="16px" height="16px" color={colors.secondary} />
                      Sponsored
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("pub.description", "Description")}
                value={formData.description || ""}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder={t("pub.descriptionPlaceholder", "Enter pub description")}
              />
            </Grid>

            {/* Contact Information Section */}
            <Grid item xs={12}>
              <Box className={classes.sectionHeader}>
                <Icon name="phone" width="20px" height="20px" color={colors.primary} />
                <Typography variant="h6" fontWeight={600}>
                  {t("pub.contactInfo", "Contact Information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.phone", "Phone")}
                value={formData.phone || ""}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder={t("pub.phonePlaceholder", "+1 234 567 8900")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.email", "Email")}
                type="email"
                value={formData.email || ""}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder={t("pub.emailPlaceholder", "pub@example.com")}
              />
            </Grid>

            {/* Location Section */}
            <Grid item xs={12}>
              <Box className={classes.sectionHeader}>
                <Icon name="map-pin" width="20px" height="20px" color={colors.primary} />
                <Typography variant="h6" fontWeight={600}>
                  {t("pub.location", "Location")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              {/*
                * RECHERCHE D'ADRESSE — remplace la saisie manuelle des
                * coordonnées.
                *
                * Le formulaire demandait à l'opérateur de TAPER latitude et
                * longitude. Depuis que le ciblage par rayon existe, c'est le
                * champ dont dépend entièrement la diffusion de la campagne, et
                * une coordonnée fausse ne se voit nulle part : la pub est
                * simplement servie à personne.
                *
                * La recherche suit la même chaîne que tout le reste de la
                * plateforme — répertoire interne d'abord, fournisseur externe
                * en repli côté serveur, persistance du lieu retenu. Un centre
                * déjà connu se choisit donc en deux frappes, sans coût.
                *
                * Ville et code postal sont volontairement laissés modifiables
                * en dessous : le libellé du fournisseur ne découpe pas toujours
                * proprement, et l'opérateur doit pouvoir corriger sans perdre
                * la position.
                */}
              <AddressAutocomplete
                label={t("pub.address", "Address")}
                value={formData.address || ""}
                onSelect={(place) => {
                  if (!place) {
                    // Adresse effacée : la position part avec elle. Garder les
                    // coordonnées d'un lieu qu'on vient de retirer donnerait
                    // une campagne ciblée sur une adresse qui n'existe plus
                    // dans le formulaire.
                    setFormData((prev) => ({
                      ...prev,
                      address: "",
                      latitude: null,
                      longitude: null,
                    }));
                    return;
                  }

                  setFormData((prev) => ({
                    ...prev,
                    address: place.raw,
                    latitude: place.lat,
                    longitude: place.lng,
                    // La ville n'est renseignée que si elle est vide : une
                    // valeur saisie à la main par l'opérateur n'a pas à être
                    // écrasée par un découpage automatique.
                    city: prev.city || place.raw.split(",").slice(-1)[0]?.trim() || prev.city,
                  }));
                }}
                onFreeText={(text) => {
                  /*
                   * Texte libre : le libellé est conservé, la position REMISE À
                   * ZÉRO.
                   *
                   * Sans cela, réécrire l'adresse après avoir choisi un lieu
                   * laisserait les anciennes coordonnées en place : un libellé
                   * et un point qui ne correspondent plus, et une campagne
                   * diffusée autour du mauvais endroit sans que rien ne le
                   * signale.
                   */
                  setFormData((prev) =>
                    prev.address === text
                      ? prev
                      : { ...prev, address: text, latitude: null, longitude: null }
                  );
                }}
                placeholder={t("pub.addressPlaceholder", "Street address")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.city", "City")}
                value={formData.city || ""}
                onChange={(e) => handleChange("city", e.target.value)}
                placeholder={t("pub.cityPlaceholder", "City name")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.postalCode", "Postal Code")}
                value={formData.postal_code || ""}
                onChange={(e) => handleChange("postal_code", e.target.value)}
                placeholder={t("pub.postalCodePlaceholder", "12345")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.latitude", "Latitude")}
                type="number"
                inputProps={{ step: "any" }}
                value={formData.latitude ?? ""}
                onChange={(e) => handleChange("latitude", e.target.value)}
                placeholder="0.000000"
                helperText={t("pub.coordinatesAuto", "Renseignée par la recherche d'adresse")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.longitude", "Longitude")}
                type="number"
                inputProps={{ step: "any" }}
                value={formData.longitude ?? ""}
                onChange={(e) => handleChange("longitude", e.target.value)}
                placeholder="0.000000"
                helperText={t("pub.coordinatesAuto", "Renseignée par la recherche d'adresse")}
              />
            </Grid>

            {/*
              * RAYON DE DIFFUSION — le champ qui décide où la pub apparaît.
              *
              * Placé dans la section Localisation et juste sous les
              * coordonnées parce qu'il n'a de sens qu'avec elles : sans
              * latitude/longitude la campagne est diffusée partout et le rayon
              * est ignoré. L'alerte ci-dessous le dit explicitement plutôt que
              * de laisser l'opérateur croire qu'il a ciblé quelque chose.
              */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.radiusKm", "Rayon de diffusion (km)")}
                type="number"
                inputProps={{ min: 1, max: PUB_MAX_RADIUS_KM, step: 1 }}
                value={formData.radius_km ?? ""}
                onChange={(e) => handleChange("radius_km", e.target.value)}
                placeholder={String(PUB_DEFAULT_RADIUS_KM)}
                helperText={t("pub.radiusHint", {
                  default: PUB_DEFAULT_RADIUS_KM,
                  max: PUB_MAX_RADIUS_KM,
                  defaultValue: `La pub s'affiche aux utilisateurs situés dans ce rayon. Vide = sans limite de distance. Défaut ${PUB_DEFAULT_RADIUS_KM} km, maximum ${PUB_MAX_RADIUS_KM} km.`,
                })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {!formData.latitude || !formData.longitude ? (
                <Alert severity="info" icon={<Icon name="map-pin" width="18px" height="18px" color={colors.primary} />}>
                  {t(
                    "pub.noCoordinatesNotice",
                    "Sans latitude ni longitude, cette pub est diffusée à tous les utilisateurs, quel que soit leur lieu. Le rayon n'est alors pas appliqué."
                  )}
                </Alert>
              ) : (
                <Alert severity="success" icon={<Icon name="map-pin" width="18px" height="18px" color={colors.success} />}>
                  {t("pub.geoTargetedNotice", {
                    radius: formData.radius_km ?? "∞",
                    defaultValue: `Diffusion ciblée : visible dans un rayon de ${formData.radius_km ?? "∞"} km autour de ce point.`,
                  })}
                </Alert>
              )}
            </Grid>

            {/* Images Section */}
            <Grid item xs={12}>
              <Box className={classes.sectionHeader}>
                <Icon name="image" width="20px" height="20px" color={colors.primary} />
                <Typography variant="h6" fontWeight={600}>
                  {t("pub.images", "Images")}
                </Typography>
              </Box>
            </Grid>

            {/* Main Image Upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                {t("pub.mainImage", "Main Image")}
              </Typography>
              <Box className={classes.imageUploadContainer}>
                <CustomDropZoneUpload
                  width="200px"
                  height="200px"
                  borderRadius="8px"
                  imageUrl={mainImagePreview}
                  onDrop={handleMainImageUpload}
                  iconName="image"
                  iconWidth="48px"
                  iconHeight="48px"
                />
                {mainImagePreview && (
                  <IconButton
                    onClick={handleRemoveMainImage}
                    className={classes.removeImageButton}
                    size="small"
                  >
                    <Icon name="x" width="16px" height="16px" color={colors.error} />
                  </IconButton>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                {t("pub.imageUploadHint", "Click or drag to upload (Max 10MB, JPG/PNG)")}
              </Typography>
            </Grid>

            {/* Gallery Images */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                {t("pub.galleryImages", "Gallery Images")}
              </Typography>
              <Box className={classes.galleryContainer}>
                {galleryPreviews.map((preview, index) => (
                  <Card key={index} className={classes.galleryImageCard}>
                    <CardMedia
                      component="img"
                      height="120"
                      image={preview}
                      alt={`Gallery ${index + 1}`}
                    />
                    <CardActions className={classes.galleryImageActions}>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveGalleryImage(index)}
                        className={classes.galleryRemoveButton}
                      >
                        <Icon name="trash-2" width="16px" height="16px" color={colors.WHITE} />
                      </IconButton>
                    </CardActions>
                  </Card>
                ))}
                
                {/* Add Gallery Image Dropzone */}
                <Box className={classes.addGalleryImage}>
                  <CustomDropZoneUpload
                    width="150px"
                    height="150px"
                    borderRadius="8px"
                    onDrop={handleGalleryImageUpload}
                    iconName="plus"
                    iconWidth="32px"
                    iconHeight="32px"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Settings Section */}
            <Grid item xs={12}>
              <Box className={classes.sectionHeader}>
                <Icon name="settings" width="20px" height="20px" color={colors.primary} />
                <Typography variant="h6" fontWeight={600}>
                  {t("pub.settings", "Settings")}
                </Typography>
              </Box>
            </Grid>

            {/*
              * RATTACHER A UN CENTRE — le champ qui bloquait l'ouverture du
              * formulaire.
              *
              * C'etait un <Select> avec un <MenuItem> PAR CENTRE. MUI monte tous
              * les enfants d'un Select ouvert : sur un catalogue de plusieurs
              * milliers de centres, ouvrir la liste construisait des milliers de
              * noeuds DOM d'un coup, sur le thread principal. D'ou le figement de
              * plusieurs secondes. Le reseau n'y etait pour rien — le rendu, si.
              *
              * Remplace par une recherche cote serveur : ~50 options quel que
              * soit le nombre de centres, une requete debouncee par terme saisi,
              * et rien du tout tant que la liste n'est pas ouverte.
              *
              * `null` reste l'instruction de DETACHER (voir handleSubmit) : vider
              * le champ envoie sign_id: null, pas une cle absente.
              */}
            <Grid item xs={12} sm={6}>
              <CentreAutocomplete
                value={formData.sign_id ?? null}
                onChange={(next) =>
                  handleChange("sign_id", typeof next === "number" ? next : null)
                }
                label={t("pub.assignToSign", "Assign to centre")}
                helperText={t(
                  "pub.assignToSignHint",
                  "Laissez vide pour une publicité générale (aucun centre)."
                )}
              />
            </Grid>

            {/*
              * RATTACHER À UNE ENSEIGNE — vendre la campagne à tout un réseau.
              *
              * C'est ce qui remplace les N copies d'une même campagne, une par
              * centre, que l'opérateur devait créer et maintenir : une seule
              * ligne s'affiche désormais dans la fiche de chaque centre du
              * réseau (App\Services\Pub::getBySign).
              *
              * Placé juste sous le sélecteur de centre parce que les deux
              * répondent à la même question — à qui cette campagne est-elle
              * vendue — à deux échelles. L'alerte ci-dessous nomme la portée
              * résultante plutôt que de laisser l'opérateur la déduire de deux
              * champs dont un seul compte.
              *
              * ⚠️ L'API refuse (422) le couple centre + enseigne quand le centre
              * n'appartient pas à cette enseigne. Le formulaire ne pré-filtre
              * PAS la liste sur le centre choisi : cela masquerait l'erreur au
              * lieu de l'expliquer, et forcerait un ordre de saisie (centre
              * d'abord) que rien à l'écran n'indique.
              */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="pub-etablissement-label">
                  {t("pub.assignToEtablissement", "Rattacher à une enseigne")}
                </InputLabel>
                <Select
                  labelId="pub-etablissement-label"
                  value={
                    formData.etablissement_id == null
                      ? ""
                      : String(formData.etablissement_id)
                  }
                  label={t("pub.assignToEtablissement", "Rattacher à une enseigne")}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // "" -> null, PAS undefined : voir handleSubmit, null est
                    // l'instruction de détacher.
                    handleChange(
                      "etablissement_id",
                      raw === "" ? null : Number(raw)
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>{t("pub.noEtablissement", "Sans enseigne")}</em>
                  </MenuItem>
                  {etablissements.map((etablissement) => (
                    <MenuItem key={etablissement.id} value={String(etablissement.id)}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Icon name="grid" width="16px" height="16px" color={colors.primary} />
                        {etablissement.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              {formData.sign_id ? (
                <Alert severity="info" icon={<Icon name="map-pin" width="18px" height="18px" color={colors.primary} />}>
                  {t(
                    "pub.scopeCentreNotice",
                    "Vendue à un centre : elle apparaît dans la fiche de ce centre uniquement."
                  )}
                </Alert>
              ) : formData.etablissement_id ? (
                <Alert severity="success" icon={<Icon name="grid" width="18px" height="18px" color={colors.success} />}>
                  {t(
                    "pub.scopeNetworkNotice",
                    "Vendue à l'enseigne : elle apparaît dans la fiche de TOUS les centres de ce réseau, sans avoir à la dupliquer."
                  )}
                </Alert>
              ) : (
                <Alert severity="info" icon={<Icon name="tag" width="18px" height="18px" color={colors.primary} />}>
                  {t(
                    "pub.scopeGeneralNotice",
                    "Campagne plateforme : aucun rattachement, elle est servie dans le flux général de l'application."
                  )}
                </Alert>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.displayOrder", "Display Order")}
                type="number"
                value={formData.display_order || ""}
                onChange={(e) => handleChange("display_order", e.target.value)}
                placeholder="0"
                helperText={t("pub.displayOrderHint", "Lower numbers appear first")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.startDate", "Start Date")}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.start_date || ""}
                onChange={(e) => handleChange("start_date", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pub.endDate", "End Date")}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.end_date || ""}
                onChange={(e) => handleChange("end_date", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active ?? true}
                    onChange={(e) => handleChange("is_active", e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>
                      {t("pub.isActive", "Active")}
                    </Typography>
                    <Chip 
                      label={formData.is_active ? "Active" : "Inactive"}
                      size="small"
                      color={formData.is_active ? "success" : "default"}
                    />
                  </Box>
                }
              />
            </Grid>

            {/* Validation Alert */}
            {!formData.name && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {t("pub.nameRequiredAlert", "Pub name is required to create/update")}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button 
            onClick={onCancel} 
            disabled={isPending}
            variant="outlined"
            startIcon={<Icon name="x" width="16px" height="16px" />}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={isPending || !formData.name}
            startIcon={<Icon name={isEdit ? "check" : "plus"} width="16px" height="16px" />}
          >
            {isEdit ? t("common.update", "Update") : t("common.create", "Create")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
